import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { translationPropType } from '@eventbrite/legacy-infra.i18n';
import { COLORS_MAP } from '@eventbrite/legacy-eds.styles/color';
import {
    SVG_PROP_TYPE,
    SIZE_PROP_TYPE,
    COLOR_PROP_TYPE,
    SHOULD_USE_RESET_PATTERN,
    VECTOR_IMAGE_ICON,
    VECTOR_IMAGE_BRAND,
    VECTOR_IMAGE_PAYMENT,
    VECTOR_IMAGE_GRAPHIC,
    VECTOR_IMAGE_ILLUSTRATION,
} from './constants';

import styles from './VectorImage.module.scss';

export class VectorImage extends PureComponent {
    static propTypes = {
        /**
         * The SVG of icon that you want to display
         * (or a component that renders and <svg />)
         */
        svg: SVG_PROP_TYPE.isRequired,
        /**
         * The type of icon that you want to display e.g. icon, brand, payment, graphic
         */
        type: PropTypes.oneOf([
            VECTOR_IMAGE_ICON,
            VECTOR_IMAGE_BRAND,
            VECTOR_IMAGE_PAYMENT,
            VECTOR_IMAGE_GRAPHIC,
            VECTOR_IMAGE_ILLUSTRATION,
        ]).isRequired,
        /**
         * Additional content to display next to svg
         */
        children: PropTypes.node,
        /**
         * The size of the icon
         */
        size: SIZE_PROP_TYPE,
        /**
         * The specified width of the icon
         */
        width: PropTypes.string,
        /**
         * The specified height of the icon
         */
        height: PropTypes.string,
        /**
         * The display the icon as a block level element
         */
        isBlockLevel: PropTypes.bool,
        /**
         * The fill color of the icon
         */
        color: COLOR_PROP_TYPE,
        /**
         * The stroke color of the icon
         */
        strokeColor: COLOR_PROP_TYPE,
        /**
         * Option to include a halo for the icon
         */
        hasHalo: PropTypes.bool,
        /**
         * Option to include a background color halo for the icon
         */
        haloColor: COLOR_PROP_TYPE,
        /**
         * Option to override the halo size.
         */
        haloSize: SIZE_PROP_TYPE,
        /**
         * Option to animate the icon
         * (Note: not all icons can be animated)
         */
        animated: PropTypes.bool,
        /**
         * Add a title attribute to the to icons markup
         */
        title: translationPropType,
        /**
         * CSS Modules `styles` object that can be used to provide a handful of type-specific
         * CSS classnames that are referenced and set in VectorImage based on certain params
         * (type, size, animated, and haloSize) but need to be defined in the consumer component's
         * *.module.scss file.
         */
        typeSpecificStyles: PropTypes.object,
        __containerClassName: PropTypes.string,
    };

    render() {
        const {
            svg,
            type,
            size,
            width,
            height,
            color,
            strokeColor,
            hasHalo,
            haloColor,
            haloSize,
            animated,
            isBlockLevel,
            title,
            typeSpecificStyles = {},
            __containerClassName,
            children,
        } = this.props;
        const sizeClassname = `${type}-${size}`;
        const animatedClassname = `${type}-animated`;

        const className = classNames(
            styles.root,
            {
                [typeSpecificStyles[sizeClassname]]: size && typeSpecificStyles[sizeClassname],
                [typeSpecificStyles[animatedClassname]]:
                    animated && typeSpecificStyles[animatedClassname],
                [styles[`color-${color}`]]: color,
                [styles[`stroke-${strokeColor}`]]: strokeColor,
                [styles.block]: isBlockLevel,
                [styles.sizeReset]:
                    SHOULD_USE_RESET_PATTERN.test(type) || (width && height) || haloSize,
                [styles.center]: haloSize,
                [styles.haloSizeOverride]: haloSize,
            },
            __containerClassName,
        );
        let hiddenText;
        let accessibilityProps = {};
        let style;

        // NOTE: According to Alli we may want to always have a `title` for accessibility
        // reasons, so it'd be nice if we had a mapping of types to display names that
        // we can fallback to if the `title` isn't specified
        if (title) {
            hiddenText = <span className={styles.hiddenAccessible}>{title}</span>;
        } else {
            accessibilityProps = { ...accessibilityProps, 'aria-hidden': true };
        }

        if (height && width) {
            style = {
                height,
                width,
            };
        }

        const icon = (
            <i
                className={className}
                title={title}
                data-testid="icon"
                style={style}
                {...accessibilityProps}
            >
                {svg}
                {hiddenText}
                {children}
            </i>
        );

        if (hasHalo) {
            let newHaloSize = size;

            if (haloSize) {
                newHaloSize = haloSize;
            }

            const haloSizeClassname = `${type}-halo--${newHaloSize}`;
            const haloClassName = classNames(
                styles.halo,
                {
                    [typeSpecificStyles[haloSizeClassname]]:
                        newHaloSize && typeSpecificStyles[haloSizeClassname],
                },
                __containerClassName,
            );
            let style = {};

            if (haloColor && COLORS_MAP[haloColor]) {
                style = { '--eds-VectorImage-halo-background-color': COLORS_MAP[haloColor] };
            }

            return (
                <span className={haloClassName} style={style} data-testid="eds-icon-halo">
                    {icon}
                </span>
            );
        }

        return icon;
    }
}
